'use client';

import { useState, useEffect, useMemo } from 'react';
import { getDistance } from 'geolib';
import type { Restaurant, Coord } from '@/app/types/restaurant';
import { createClient } from '@/lib/supabase/client';

const ITEMS_PER_PAGE = 20;

type UseRestaurantsParams = {
  userLocation?: Coord;
  search: string;
  filterDistrict: string;
  filterMon: string;
  showOpen: boolean;
  radius: number;
  isOpenNow: (gioMo: string) => boolean;
  initialData?: Restaurant[];
};

export function useRestaurants({
  userLocation, search, filterDistrict, filterMon, showOpen, radius, isOpenNow, initialData,
}: UseRestaurantsParams) {
  const [currentPage, setCurrentPage] = useState(1);
  const [allRestaurants, setAllRestaurants] = useState<Restaurant[]>(initialData ?? []);
  const [isLoading, setIsLoading] = useState(!initialData);

  // Fetch from Supabase only if no SSR data provided
  useEffect(() => {
    if (initialData && initialData.length > 0) return;

    const supabase = createClient();
    if (!supabase) {
      setIsLoading(false);
      return;
    }

    supabase
      .from('restaurants')
      .select('*')
      .order('stt')
      .then(({ data, error }) => {
        if (error) {
          if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch restaurants:', error);
        } else {
          setAllRestaurants((data as Restaurant[]) || []);
        }
        setIsLoading(false);
      });
  }, [initialData]);

  // Unique districts and food types derived from fetched data
  const districts = useMemo(() => {
    return Array.from(new Set(allRestaurants.map((r) => r.district).filter(Boolean))).sort();
  }, [allRestaurants]);

  const foodTypes = useMemo(() => {
    return Array.from(new Set(allRestaurants.map((r) => r.phan_loai).filter(Boolean))).sort();
  }, [allRestaurants]);

  // Filter, sort, and calculate distance
  const filtered = useMemo(() => {
    return allRestaurants
      .map((r) => {
        let distance: number | undefined;
        if (userLocation && r.lat && r.lng) {
          try {
            distance = getDistance(
              { latitude: userLocation.lat, longitude: userLocation.lng },
              { latitude: r.lat, longitude: r.lng }
            );
          } catch {
            // ignore distance calc errors
          }
        }
        return { ...r, distance };
      })
      .filter((r) => {
        const searchLower = search.toLowerCase();
        const matchSearch =
          search === '' ||
          r.ten_quan.toLowerCase().includes(searchLower) ||
          r.ten_mon?.toLowerCase().includes(searchLower) ||
          r.dia_chi?.toLowerCase().includes(searchLower) ||
          r.district?.toLowerCase().includes(searchLower) ||
          r.street_name?.toLowerCase().includes(searchLower) ||
          r.ward?.toLowerCase().includes(searchLower);
        const matchDistrict = filterDistrict === '' || r.district === filterDistrict;
        const matchMon = filterMon === '' || r.phan_loai === filterMon;
        const matchOpen = !showOpen || isOpenNow(r.gio_mo);
        const matchRadius = !userLocation || !r.distance || r.distance <= radius;
        return matchSearch && matchDistrict && matchMon && matchOpen && matchRadius;
      })
      .sort((a, b) => {
        if (userLocation && a.distance && b.distance) return a.distance - b.distance;
        return 0;
      });
  }, [allRestaurants, userLocation, search, filterDistrict, filterMon, showOpen, radius, isOpenNow]);

  // Pagination
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedItems = filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterDistrict, filterMon, showOpen, userLocation, radius]);

  return {
    filtered,
    paginatedItems,
    totalPages,
    currentPage,
    setCurrentPage,
    districts,
    foodTypes,
    isLoading,
  };
}
